<template>
    <div class="video-chat mt-page">
        <b-form autocomplete="off"
                @submit.stop.prevent="onJoin">
            <b-container class="p-0">
                <vue-webrtc ref="webrtc"
                            width="100%"
                            :roomId="room"
                            :enableLogs="true"
                            :muted="muted"
                            :show="show"
                            :audioSource="audioSource"
                            :audioOutput="audioOutput"
                            :videoSource="videoSource"
                            :peerOptions="peerOptions"
                            v-on:joined-room="logEvent"
                            v-on:left-room="logEvent"
                            v-on:opened-room="logEvent"
                            v-on:share-started="shareEvent"
                            v-on:share-stopped="logEvent"
                            @error="onError"/>
                <b-row v-if="connected" class="mt-4">
                    <b-col class="text-center"><b-button size="sm" :variant="muted ? 'danger' : 'primary'" @click="onMute">
                        {{mutedLabel}}
                        <shs-icon name="microphone"/>
                    </b-button></b-col>
                    <b-col class="text-center"><b-button size="sm" :variant="show ? 'primary' : 'danger'" @click="onShow">
                        {{showLabel}}
                        <shs-icon name="camera-slr"/>
                    </b-button></b-col>
                    <b-col class="text-center"><b-button size="sm" :variant="share ? 'danger' : 'primary'" @click="onShare">
                        {{shareLabel}}
                        <shs-icon name="monitor"/>
                    </b-button></b-col>
                    <b-col class="text-center"><b-button size="sm" variant="danger" @click="onLeave">
                        Leave Room
                        <shs-icon name="x"/>
                    </b-button></b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col class="text-center">
                        <label for="room" class="text-nowrap">
                            {{ 'Room Identifier' }}<br/>
                            <b-form-input id="room"
                                          v-model="room"
                                          :disabled="!!roomId"
                                          placeholder="enter room identifier"></b-form-input>
                        </label>
                    </b-col>
                    <b-col class="text-center">
                        <label for="audioSource" class="text-nowrap">
                            {{ 'Audio Input' }}<br/>
                            <b-form-select v-model="audioSource" :options="audioSources"/>
                        </label>
                    </b-col>
                    <b-col class="text-center">
                        <label for="audioOutput" class="text-nowrap">
                            {{ 'Audio Output' }}<br/>
                            <b-form-select v-model="audioOutput" :options="audioOutputs"/>
                        </label>
                    </b-col>
                    <b-col class="text-center">
                        <label for="videoSource" class="text-nowrap">
                            {{ 'Video Source' }}<br/>
                            <b-form-select v-model="videoSource" :options="videoSources"/>
                        </label>
                    </b-col>
                </b-row>
                <b-row v-if="!connected" class="mt-5">
                    <b-col class="text-center"><b-button size="sm" :disabled="!room" @click="onJoin">Join Room</b-button></b-col>
                </b-row>
            </b-container>
        </b-form>
    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import VueWebRTC from '@/components/VueWebRTC'
import router from "@/apps/default/router";

export default Vue.extend({
    name: 'VideoCall',
    props: {
        roomId: {
            type: String,
            required: false,
            default: null,
        }
    },
    components: {
        'vue-webrtc': VueWebRTC
    },
    data() {
        return {
            room: null,
            peerOptions: {
                iceServers: [
                    {
                        // https://raw.githubusercontent.com/pradt2/always-online-stun/master/valid_hosts.txt
                        urls: 'stun:stun2.l.google.com:19302'
                        //urls: 'turn:turn.sustainable-health.app',
                        //username: 'myuser',
                        //credential: 'mypassword'
                    }
                ]
            },
            audioSource: null,
            audioOutput: null,
            videoSource: null,
            audioSources: {},
            audioOutputs: {},
            videoSources: {},
            connected: false,
            muted: false,
            mutedLabel: 'Mute',
            show: true,
            showLabel: 'Hide Video',
            share: null,
            shareLabel: 'Share Screen'
        }
    },
    mounted() {
        console.log('VideoCall:mounted  roomId=', this.roomId)
        // default room
        if (this.roomId)
            this.room = this.roomId
        // stop any current streams
        if (window.stream) {
            window.stream.getTracks().forEach(track => {
                track.stop()
            })
        }
        // get default audio/video devices
        const constraints = {
            audio: {deviceId: undefined},
            video: {deviceId: undefined}
        }
        if (navigator.mediaDevices)
            navigator.mediaDevices.getUserMedia(constraints)
                .then(stream => {
                    // make stream available to console
                    window.stream = stream
                    // refresh button list in case labels have become available
                    return navigator.mediaDevices.enumerateDevices()
                })
                .then(devices => {
                    //console.log('VideoCall:mounted  devices=', devices)
                    // handles being called several times to update labels. Preserve values.
                    //const values = selectors.map(select => select.value)
                    // cache values
                    let audioSourceValue = this.audioSource
                    let audioOutputValue = this.audioOutput
                    let videoSourceValue = this.videoSource
                    // clear lists
                    let audioSources = {}
                    let audioOutputs = {}
                    let videoSources = {}
                    // add new selections
                    devices.forEach(device => {
                        // create and append option
                        const deviceId = device.deviceId
                        if (device.kind === 'audioinput')
                            audioSources[deviceId] = device.label || `microphone ${audioSources.length + 1}`
                        else if (device.kind === 'audiooutput')
                            audioOutputs[deviceId] = device.label || `speaker ${audioOutputs.length + 1}`
                        else if (device.kind === 'videoinput')
                            videoSources[deviceId] = device.label || `camera ${videoSources.length + 1}`
                        else
                            console.log('VideoCall:mounted  unknown device=', device)
                    })
                    // restore selection
                    this.audioSources = audioSources;
                    this.audioOutputs = audioOutputs;
                    this.videoSources = videoSources;
                    // soon...
                    this.$nextTick(() => {
                        // default sources and outputs
                        if (!this.audioSource && this.audioSources)
                            this.audioSource = Object.keys(this.audioSources)[0]
                        if (!this.audioOutput && this.audioOutputs)
                            this.audioOutput = Object.keys(this.audioOutputs)[0]
                        if (!this.videoSource && this.videoSources)
                            this.videoSource = Object.keys(this.videoSources)[0]
                        // autojoin
                        if (this.$route.query && this.$route.query.join)
                            this.$nextTick(() => this.onJoin())
                    })
                })
                .catch(error => {
                    console.log('VideoCall:mounted  error=', error.message, error.name)
                })
        else
            router.app.$toasted.error('Unable to access media devices', {duration: 5000})
    },
    watch: {
        muted() {
            this.mutedLabel = this.muted ? 'Unmute' : 'Mute'
        },
        show() {
            this.showLabel = this.show ? 'Stop Video' : 'Show Video'
        },
        share() {
            this.shareLabel = this.share ? 'Unshare Screen' : 'Share Screen'
        }
    },
    methods: {
        resizeScreen() {
            console.log('VideoCall:resizeScreen')
        },
        onJoin() {
            console.log('VideoCall:updateRoom')
            if (this.$refs.webrtc) {
                this.$refs.webrtc.join()
                this.connected = true
            }
        },
        onLeave() {
            if (this.$refs.webrtc) {
                this.$refs.webrtc.leave()
                this.connected = false
            }
        },
        onShare() {
            if (this.$refs.webrtc) {
                if (this.share) {
                    this.$refs.webrtc.unshare(this.share)
                    this.share = null
                } else
                    this.$refs.webrtc.share()
            }
        },
        onError(error, stream) {
            console.log('VideoChat.onError  error=', error, stream)
        },
        shareEvent(share) {
            this.share = share
        },
        logEvent(event) {
            console.log('VideoChat.logEvent  event=', event)
        },
        onMute() {
            this.muted = !this.muted
        },
        onShow() {
            this.show = !this.show
        }
    }
})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.video-chat {
}
</style>
